import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import TagService from '../../Services/TagService';

export default memo(forwardRef(TextTagsWidget));

function TextTagsWidget({ asset, widget }, ref) {
  const [state, updateState] = useState({
    assetTagsMap: new Map(),
    dataLoading: true,
  });

  const source = CancelToken.source();

  const fetchAssetTags = async () => {
    try {
      const params = { orgID: asset.orgID };
      const assetTags = await new TagService().fetchAllTags(
        asset._id,
        params,
        source.token
      );
      const assetTagsMap = new Map(
        assetTags.map(({ name, value }) => [name, value])
      );
      updateState({ ...state, assetTagsMap, dataLoading: false });
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, dataLoading: false });
    }
  };

  useImperativeHandle(ref, () => ({ fetchAssetTags }));

  useEffect(() => {
    fetchAssetTags();
    return source.cancel;
  }, []);

  return (
    <div className="w-100 overflow-auto px-3">
      {state.dataLoading ? (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <FontAwesomeIcon
            color="var(--brand-primary)"
            icon={faCircleNotch}
            size="2x"
            spin
          />
        </div>
      ) : (
        <table className="table table-borderless table-sm">
          <thead>
            <tr>
              <th
                className="text-left font-weight-light sticky-top bg-white text-text-light-gray"
                scope="col"
              >
                Name
              </th>
              <th
                className="text-left font-weight-light sticky-top bg-white text-text-light-gray"
                scope="col"
              >
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {(widget?.settings?.tags ?? []).map((name, i) => (
              <tr className="text-break" key={`${name}-${i}`}>
                <th className="text-muted font-weight-medium text-left">
                  {name}
                </th>
                <th className="text-muted font-weight-medium text-left">
                  {state.assetTagsMap.get(name) || ''}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

TextTagsWidget.propTypes = {
  asset: PropTypes.shape({ _id: PropTypes.string, orgID: PropTypes.string }),
  widget: PropTypes.shape({
    settings: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
};

TextTagsWidget.defaultProps = { asset: {}, widget: { settings: {} } };
